import { createSelector, Dispatch } from '@reduxjs/toolkit';
import {
    selectIsCalendarLoading,
    selectIsSchedulesEmpty,
    selectOfferLevelCalendarsFromCalendarState,
    selectSchedulesFromCalendarState,
} from 'component/page/detail/store/calendar.selectors';
import {
    selectActivityIdFromSelectedOffer,
    selectAvailabilityIsLoading,
    selectBasedCalendarOfferIds,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectOfferLevelSelectedDate,
    selectOfferOptionTokenFromOfferSelected,
    selectPossibleOfferOptions,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { selectActivityTokenFromDetailState } from 'component/page/detail/store/detail.selectors';
import { ActivityOfferToken, OfferLevelSchedule } from 'core/type/Details.types';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { selectBehaviorFromBootstrap, selectCmsFromBootstrap, selectLocaleFromBootstrap } from 'store/slice/bootstrap';
import { fetchAvailability, setOfferNotification, setOfferOptionSelectedDate } from 'component/page/detail/store/availabilityIndex';
import { OfferNotificationType } from 'component/page/detail/store/availabilityIndex.types';
import { getCheapestSchedules } from 'component/page/detail/PopoverCalendar/PopoverCalendarUtils';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { ActivitiesBehavior } from 'core/type/behaviors.types';

interface StateProps {
    cms: CmsValue;
    locale: string;
    isScheduleReady: boolean;
    offerLevelSchedules: OfferLevelSchedule[];
    offerLevelSelectedDate: Date;
    selectedOfferId: number;
    activityTokenFromState: string;
    offerOptionToken: string;
    screenSize: ScreenSize;
    behaviors: ActivitiesBehavior;
    activityId: number | undefined;
}

interface DispatchProps {
    setSelectedDate: (offerToken: ActivityOfferToken, offerId: number, selectedDate: Date) => void;
}

interface OwnProps {
    isShowCalendar: boolean;
    onCalendarClick: () => void;
}

export type Props = StateProps & DispatchProps & OwnProps;

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectLocaleFromBootstrap,
    selectIsSchedulesEmpty,
    selectIsCalendarLoading,
    selectOfferLevelCalendarsFromCalendarState,
    selectOfferLevelSelectedDate,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectActivityTokenFromDetailState,
    selectOfferOptionTokenFromOfferSelected,
    selectSchedulesFromCalendarState,
    selectPossibleOfferOptions,
    selectAvailabilityIsLoading,
    selectBasedCalendarOfferIds,
    selectScreenSize,
    selectBehaviorFromBootstrap,
    selectActivityIdFromSelectedOffer,
    (
        cms,
        locale,
        isSchedulesEmpty,
        isCalendarLoading,
        offerCalendars,
        offerLevelSelectedDate,
        selectedOfferId,
        activityTokenFromState,
        offerOptionToken,
        calendarSchedules,
        possibleOffers,
        isAvailabilityLoading,
        basedCalendarOfferIds,
        screenSize,
        behaviors,
        activityId,
    ): StateProps => {
        const cheapestSchedules = getCheapestSchedules(possibleOffers, calendarSchedules, offerCalendars, basedCalendarOfferIds);
        return {
            cms,
            locale,
            offerLevelSelectedDate,
            selectedOfferId,
            activityTokenFromState,
            offerOptionToken,
            screenSize,
            offerLevelSchedules: cheapestSchedules,
            isScheduleReady: !isSchedulesEmpty && !isCalendarLoading && !isAvailabilityLoading,
            behaviors,
            activityId,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setSelectedDate: (offerToken: ActivityOfferToken, offerId: number, selectedDate: Date) => {
        dispatch(setOfferOptionSelectedDate({ offerId, selectedDate }));
        dispatch(setOfferNotification({ [OfferNotificationType.DateChip]: false }));
        fetchAvailability(offerToken.activityToken, { excludePassengers: false, enableOfferFilter: offerToken.enableOfferFilter });
    },
});
